import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import CountryLanguageWriteDTO from '../../../../../../types/country-language/CountryLanguageWriteDTO';
import CountryWriteDTO from '../../../../../../types/country/CountryWriteDTO';
import Language from '../../../../../../types/language/Language';
import { CountryService } from '../../../../services/country.service';
import { LanguageService } from '../../../../services/language.service';

@Component({
    selector: 'app-update-country-dialog',
    templateUrl: './update-country-dialog.component.html',
    styleUrls: [ './update-country-dialog.component.scss' ],
    imports: [
        DialogModule,
        InputTextModule,
        ButtonModule,
        FormsModule,
        IconField,
        InputIcon,
        IftaLabelModule,
        MultiSelect,
    ]
})
export class UpdateCountryDialogComponent implements OnInit, OnChanges {
    @Input() visible: boolean = false;
    @Input() country: CountryWriteDTO = new CountryWriteDTO({});
    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() countryUpdated: EventEmitter<CountryWriteDTO> = new EventEmitter<CountryWriteDTO>();

    public availableLanguages: Language[] = [];
    public selectedLanguages: Language[] = [];

    public loading = false;
    public loadingLanguages = false;

    constructor(
        private countryService: CountryService,
        private languageService: LanguageService,
        private messageService: MessageService
    ) {
    }

    ngOnInit(): void {
        this.loadLanguages();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['country'] && this.availableLanguages.length > 0) {
            this.updateSelectedLanguages();
        }
    }

    get saveDisabled(): boolean {
        return !this.country?.name || this.selectedLanguages.length === 0;
    }

    public close() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    public loadLanguages() {
        this.loadingLanguages = true;
        this.languageService
            .getAllLanguages()
            .then((languages) => {
                this.availableLanguages = languages;
                this.updateSelectedLanguages();
            })
            .catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.loadingLanguages = false;
            });
    }

    private updateSelectedLanguages() {
        if (this.country.languages) {
            this.selectedLanguages = this.availableLanguages.filter((language) =>
                this.country.languages.some((cl) => cl.languageId === language.id)
            );
        }
    }

    public updateCountry() {
        this.loading = true;

        // Prepare country.languages by reusing existing CountryLanguageWriteDTOs
        const existingCountryLanguagesMap = new Map(
            this.country.languages.map((cl) => [
                cl.languageId,
                cl
            ])
        );

        // Filter to determine which languages to retain and which to add
        const updatedLanguages: CountryLanguageWriteDTO[] = [];
        const existingLanguageIds = new Set<number>();

        this.selectedLanguages.forEach((language) => {
            if (existingCountryLanguagesMap.has(language.id)) {
                // Retain the existing relationship
                updatedLanguages.push(existingCountryLanguagesMap.get(language.id)!);
                existingLanguageIds.add(language.id);
            } else {
                // Add new relationship
                updatedLanguages.push(new CountryLanguageWriteDTO({ languageId: language.id }));
            }
        });

        // Remove any relationships that are no longer selected
        this.country.languages = updatedLanguages;

        this.countryService
            .updateCountry(this.country)
            .then(() => {
                this.countryUpdated.emit(this.country);
                this.close();
            })
            .catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
